/* @pepi/ui — useTheme
   Estado de apariencia compartido por web (@pepi/web) y móvil
   (@pepi/mobile). Persiste la preferencia del usuario y aplica el
   atributo data-theme sobre <html>, que activa la paleta nocturna
   definida en tokens.css (:root[data-theme='dark']).

   Modos:
     'light'   — siempre claro
     'dark'    — siempre oscuro (modo noche)
     'system'  — sigue la preferencia del sistema operativo
*/
import { computed, ref, readonly } from 'vue'

export type ThemeMode = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'pepi-theme'

/** Estado global a nivel de módulo (singleton entre todas las vistas). */
const mode = ref<ThemeMode>('system')
const systemPrefersDark = ref(false)

/** Tema efectivo tras resolver 'system'. */
const resolvedTheme = computed<ResolvedTheme>(() =>
  mode.value === 'system'
    ? (systemPrefersDark.value ? 'dark' : 'light')
    : mode.value
)

let initialized = false
let mediaQuery: MediaQueryList | null = null

function readStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored
    }
  } catch {
    // localStorage no disponible (SSR, modo privado): usar 'system'
  }
  return 'system'
}

/** Refleja el tema resuelto en el DOM. */
function applyTheme(): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (resolvedTheme.value === 'dark') {
    root.setAttribute('data-theme', 'dark')
  } else {
    root.removeAttribute('data-theme')
  }
}

/**
 * Inicializa el sistema de temas: lee la preferencia guardada,
 * escucha los cambios del sistema y aplica el tema. Idempotente,
 * así que puede llamarse de forma temprana (antes de montar la app)
 * para evitar el parpadeo claro→oscuro.
 */
export function initTheme(): void {
  if (initialized) return
  initialized = true

  mode.value = readStoredMode()

  if (typeof window !== 'undefined' && window.matchMedia) {
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    systemPrefersDark.value = mediaQuery.matches
    const onChange = (e: MediaQueryListEvent): void => {
      systemPrefersDark.value = e.matches
      applyTheme()
    }
    // addEventListener no existe en navegadores muy antiguos
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', onChange)
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(onChange)
    }
  }

  applyTheme()
}

/** Cambia y persiste el modo de apariencia. */
export function setThemeMode(next: ThemeMode): void {
  mode.value = next
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // Persistencia no disponible: el cambio dura solo esta sesión
  }
  applyTheme()
}

/**
 * Composable de apariencia. Devuelve el estado reactivo y las
 * acciones para usarlo desde cualquier componente.
 */
export function useTheme() {
  if (!initialized) initTheme()
  return {
    /** Preferencia elegida por el usuario ('light' | 'dark' | 'system'). */
    mode: readonly(mode),
    /** Tema realmente aplicado ('light' | 'dark'), ya resuelto. */
    resolvedTheme,
    /** true si la UI está en modo noche. */
    isDark: computed(() => resolvedTheme.value === 'dark'),
    setMode: setThemeMode,
  }
}
